import { ExternalLink, MediaCard, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import {
  adminB2bOnlyGraphic,
  adminHideShowGraphic,
  adminMinQtyGraphic,
  adminMulticurrencyGraphic,
  adminOrderButtonsGraphic,
  adminPreOrderGraphic,
  adminPriceList1Graphic,
  adminPriceList2Graphic,
  adminRegistrationGraphic,
  adminShippingGraphic,
} from '../assets/images';

const PAGE = 'features:storefront_widgets.pages.storefront_widgets_page.tips' satisfies I18nPrefix;

const TIP_KEYS = [
  'tiered_pricing',
  'pack_sizing',
  'registration_form',
  'b2b_only_content',
  'hide_show_products',
  'multi_add',
  'pre_order',
  'min_quantities',
  'b2b_shipping',
  'multi_currency',
] as const;

type TipKey = (typeof TIP_KEYS)[number];

type Tip = {
  key: TipKey;
  image: string;
  link: string;
  internal?: boolean;
};

const TIPS: Tip[] = [
  {
    key: 'tiered_pricing',
    image: adminPriceList1Graphic,
    link: 'https://docs.sparklayer.io/quantity-pricing',
  },
  {
    key: 'pack_sizing',
    image: adminPriceList2Graphic,
    link: 'https://docs.sparklayer.io/quantity-rules',
  },
  {
    key: 'registration_form',
    image: adminRegistrationGraphic,
    link: 'https://docs.sparklayer.io/shopify-customisations#odpwO',
  },
  {
    key: 'b2b_only_content',
    image: adminB2bOnlyGraphic,
    link: 'https://docs.sparklayer.io/shopify-customisations#kWFPB',
  },
  {
    key: 'hide_show_products',
    image: adminHideShowGraphic,
    link: 'https://docs.sparklayer.io/shopify-customisations#kWFPB',
  },
  {
    key: 'multi_add',
    image: adminOrderButtonsGraphic,
    link: 'https://docs.sparklayer.io/product-display#cXBGG',
  },
  {
    key: 'pre_order',
    image: adminPreOrderGraphic,
    link: 'https://docs.sparklayer.io/stock-display#A9XcX',
  },
  {
    key: 'min_quantities',
    image: adminMinQtyGraphic,
    link: 'https://docs.sparklayer.io/quantity-rules#jPp-0',
  },
  {
    key: 'b2b_shipping',
    image: adminShippingGraphic,
    link: '/configuration/shipping',
    internal: true,
  },
  {
    key: 'multi_currency',
    image: adminMulticurrencyGraphic,
    link: 'https://docs.sparklayer.io/shopify-markets',
  },
];

export function TipsTab() {
  const { navigationHandler } = useHandlers();

  function openTip(tip: Tip) {
    if (tip.internal) {
      navigationHandler.navigate(tip.link);
      return;
    }
    window.open(tip.link, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="flex flex-col gap-4">
      <Text size="sm" variant="secondary">
        {t(`${PAGE}.description`)}{' '}
        <ExternalLink link="https://docs.sparklayer.io/shopify-customisations">
          {t(`${PAGE}.guide`)}
        </ExternalLink>
      </Text>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {TIPS.map((tip) => (
          <MediaCard
            key={tip.key}
            headingLevel="h3"
            title={t(`${PAGE}.items.${tip.key}.title`)}
            description={t(`${PAGE}.items.${tip.key}.description`)}
            imgSrc={tip.image}
            action={{ content: t(`${PAGE}.learn_more`), onClick: () => openTip(tip) }}
          />
        ))}
      </div>
    </div>
  );
}
